import {clamp, range} from '@benchling/js-utils';
import produce from 'immer';
import reactUpdate from 'immutability-helper';
import React from 'react';
import _ from 'underscore';

import {assert, assertAndContinue} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import * as benchlingUtil from 'BenchlingUtil';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import AutomationFileUtil from 'lab-automation/automation-files/AutomationFileUtil';
import {ClientId} from 'notebook/ELNConstants';
import {ColumnDataType, guessColumnDataType} from 'notebook/structured-tables/columns/ColumnDataType';
import {ColumnDefinition} from 'notebook/structured-tables/columns/ColumnDefinitions';
import {
  GenericStructuredTableColumn,
  StructuredTableColumn,
  StructuredTableKind,
  StructuredTableMetadata,
} from 'notebook/structured-tables/StructuredTableConstants';
import {
  Cell,
  CELL_UI_PROPERTIES,
  FormulaAlignment,
  TableCellEditorPosition,
} from 'notebook/tables/constants/CellConstants';
import {Cells, Row} from 'notebook/tables/constants/RowConstants';
import {
  COLUMN_FIELD_DEFAULTS,
  ColumnConstraint,
  TableColumnType,
} from 'notebook/tables/constants/TableColumnConstants';
import {
  StructuredTableToTableMetadata,
  Table,
  TableData,
  TableDimensionLocks,
} from 'notebook/tables/constants/TableConstants';
import {
  TABLE_DEFAULT_COLUMN_WIDTH,
  TABLE_EXTRA_GHOST_COLUMN_COUNT,
  TABLE_FIRST_COL_WIDTH,
  TABLE_MAX_COLUMNS,
  TABLE_MIN_COLUMNS_WITH_GHOSTS,
  TABLE_WELL_DEFAULT_WIDTH,
  TABLE_DOUBLE_SELECTED_BORDER_WIDTH,
  TABLE_CELL_BORDER_WIDTH,
} from 'notebook/tables/constants/TableLayoutConstants';
import {
  MultiSelectionBox,
  TABLE_COLUMN,
  TABLE_ROW,
  TABLE_SELECTION_COLUMN,
  TABLE_SELECTION_ROW,
  TableDimension,
  TableIndex,
} from 'notebook/tables/constants/TableSelectionConstants';
import excess from 'notebook/tables/excess';
import {TableOpOptions} from 'notebook/tables/excess/ops/TableOpConstants';
import getPlainTextTable from 'notebook/tables/export/getPlainTextTable';
import makeTableHTML from 'notebook/tables/export/makeTableHTML';
import {isBaseBoolean, isNumeric, isTypedValueEmpty} from 'notebook/tables/typed-values/DataTypes';
import {BaseTypedValueType} from 'notebook/tables/typed-values/TypedValueConstants';
import {
  hasNLO,
  isMixtureGroup,
  isMixtureIngredientsStructuredTable,
  isNoteLinkedAssayData,
  isPlainTable,
  isStructuredTable,
} from 'notebook/TypePredicates';
import {
  AnyStructuredTable,
  isNotePartWithItem,
  Note,
  NoteLinkedObject,
} from 'notebook/util/NoteUtilConstants';
import notifications from 'notifications';
import {NoteLinkedAssayData} from 'runs/AttachmentConstants';
import * as ExportUtil from 'util/ExportUtil';
import * as logging from 'util/Logging';
import {omit} from 'util/omitKey';
import {MakeFieldsOptional, Optional} from 'util/Types';

import {EvaluatedCell, EvaluatedTable} from './excess/Evaluator';

const TableUtil = {
  /**
   * Utility method to do a one-time modification to a server-provided table into a form that the store
   * and any views can use readily. This method CAN mutate the existing table, and will return the
   * modified version.
   *
   * This method will fill in default values for any unspecified column fields.
   */
  prepareForStore(table: MakeFieldsOptional<Table, keyof typeof COLUMN_FIELD_DEFAULTS>): Table {
    // Fill in default values
    Object.entries(COLUMN_FIELD_DEFAULTS).forEach(([columnField, defaultValue]) => {
      if (table.hasOwnProperty(columnField)) {
        // @ts-expect-error
        table[columnField] = table[columnField].map((value) => (value != null ? value : defaultValue));
      } else {
        // @ts-expect-error
        table[columnField] = benchlingUtil.repeat([defaultValue], table.table[0].length);
      }
    });
    // Detect any unexpected typed values in this table
    for (const [rowIndex, row] of table.table.entries()) {
      for (const [columnIndex, cell] of row.entries()) {
        if (cell.typedValue != null) {
          logging.warn('Detected unexpected typedValue within a regular ELN table', {
            extra: {table, rowIndex, columnIndex},
          });
        }
      }
    }
    return table as Table;
  },

  /**
   * Compute the number of columns that should be rendered for a table, including ghost cells.
   *
   * If `hasGhostCells`, and we are not at the width limit, we ensure we have at least that we have
   * TABLE_MIN_COLUMNS_WITH_GHOSTS columns with at least TABLE_EXTRA_GHOST_COLUMN_COUNT ghost columns.
   */
  getColumnCount(table: Table, hasGhostCells: boolean): number {
    const actualCount = table.table[0].length;
    if (!hasGhostCells) {
      return actualCount;
    }
    return clamp(
      TABLE_MIN_COLUMNS_WITH_GHOSTS,
      TABLE_MAX_COLUMNS,
      actualCount + TABLE_EXTRA_GHOST_COLUMN_COUNT
    );
  },

  defaultContent(
    tableName: string,
    rows: number,
    cols: number,
    {isWellPlate = false}: {isWellPlate?: boolean} = {}
  ): Table {
    if (!assertAndContinue(rows != null, 'TableUtil.defaultContent called without rows')) {
      rows = 2;
    }
    if (!assertAndContinue(cols != null, 'TableUtil.defaultContent called without cols')) {
      cols = 2;
    }
    return _.chain(COLUMN_FIELD_DEFAULTS)
      .mapObject((defaultValue) => benchlingUtil.repeat([defaultValue], cols))
      .extend({
        table: TableUtil.__range__(0, rows, false).map(() =>
          TableUtil.__range__(0, cols, false).map(() => ({text: ''}))
        ),
        headerRow: false,
        headerCol: false,
        name: tableName,
      })
      .extend(
        isWellPlate
          ? {
              lockedDims: true,
              // eslint-disable-next-line no-restricted-syntax
              widths: _.times(cols, () => TABLE_WELL_DEFAULT_WIDTH),
              // eslint-disable-next-line no-restricted-syntax
              columnLabels: _.times(cols, (i) => String(excess.util.rowIndexToNumber(i))),
              // eslint-disable-next-line no-restricted-syntax
              rowLabels: _.times(rows, (i) => String(excess.util.colNumberToLetters(i))),
            }
          : undefined
      )
      .value() as unknown as Table;
  },

  firstColumnWidth(table: Table): number {
    return table._firstColumnWidth != null ? table._firstColumnWidth : TABLE_FIRST_COL_WIDTH;
  },

  /** Returns the effective frozen column count: user-set frozenColumnCount, or adapter default. Clamped to the number of non-single-input columns. */
  getAffixedColumnCount(table: Table): number {
    const raw = table.frozenColumnCount ?? table.defaultAffixedColumnCount ?? 0;
    if (raw <= 0) {
      return 0;
    }
    const siBoundary = TableUtil.findSingleInputBoundaryIndex(table);
    const totalColumns = table.table[0]?.length ?? 0;
    return Math.min(raw, totalColumns - siBoundary);
  },

  /** Absolute column index of the first non-frozen column, or 0 if none are frozen. */
  getFreezeBoundary(table: Table): number {
    const frozen = TableUtil.getAffixedColumnCount(table);
    if (frozen <= 0) {
      return 0;
    }
    return TableUtil.findSingleInputBoundaryIndex(table) + frozen;
  },

  /**
   * Map over the various types of columns present in a table. Useful for rendering a row or computing widths.
   *
   * @param {number} options.normalCellsCount - number of normal columns in a row
   * @param {number} options.ghostCellsCount - number of ghost cells after the normal columns
   * @param {number | null} options.sliceStart -
   *   start index relative of normal columns
   * @param {number | null} options.sliceEnd -
   *   end index (excl.) relative of normal columns, null to indicate through end of ghost cells
   * @param {Function} options.mapLeadingCell - should output a value for the axis column
   * @param {Function} options.mapNormalCell - should output a value per each normal column index
   * @param {Function} options.mapGhostCell - should output a value per each ghost column index
   * @return {Array} - an array of the output values from mapping, filtered to non-null values.
   */

  mapColumns({
    normalCellsCount,
    ghostCellsCount,
    sliceStart,
    sliceEnd,
    mapLeadingCell,
    mapNormalCell,
    mapGhostCell,
  }: {
    normalCellsCount: number;
    ghostCellsCount: number;
    sliceStart: number | null;
    sliceEnd: number | null;
    mapLeadingCell: () => JSX.Element | string | number;
    mapNormalCell: (n: number) => JSX.Element | number | null;
    mapGhostCell: (n: number) => JSX.Element | number;
  }): Array<string | JSX.Element | number | null> {
    const cells: Array<string | number | JSX.Element | null> = [mapLeadingCell()];
    // a null sliceStart can be treated as just the beginning of the table
    sliceStart = sliceStart ?? 0;
    if (sliceEnd == null) {
      sliceEnd = normalCellsCount + ghostCellsCount;
    }
    for (let j = sliceStart; j < sliceEnd; j++) {
      if (j < normalCellsCount) {
        cells.push(mapNormalCell(j));
      } else {
        cells.push(mapGhostCell(j));
      }
    }
    return cells.filter((cell) => cell != null);
  },

  tableWidth(
    table: Table,
    hasGhostCells: boolean = false,
    {sliceStart, sliceEnd}: {sliceStart: number | null; sliceEnd: number | null} = {
      sliceStart: null,
      sliceEnd: null,
    }
  ): number {
    const cellWidths = TableUtil.mapColumns({
      normalCellsCount: table.widths.length,
      ghostCellsCount: TableUtil.getColumnCount(table, hasGhostCells) - table.widths.length,
      sliceStart,
      sliceEnd,
      mapLeadingCell: () => TableUtil.firstColumnWidth(table),
      mapNormalCell: (j) => table.widths[j],
      mapGhostCell: () => TABLE_DEFAULT_COLUMN_WIDTH,
    });
    return benchlingUtil.sum(cellWidths);
  },

  /**
   * Return an object with the bounds of a box as an object given two 2D
   * indices representing the corners of the box.
   */
  indicesToCorners(
    start: TableIndex,
    end: TableIndex
  ): {minI: number; maxI: number; minJ: number; maxJ: number} {
    const minI = Math.min(start[0], end[0]);
    const maxI = Math.max(start[0], end[0]);
    const minJ = Math.min(start[1], end[1]);
    const maxJ = Math.max(start[1], end[1]);
    return {minI, maxI, minJ, maxJ};
  },

  /**
   * Utility method to return the cell alignment. Returns the `align` if
   * explicitly set, `right` if the evaluated value is a number, else `left`.
   *
   * @param  {Object}  cell           The cell's properties.
   * @param  {Object}  evaluatedCell  The evaluated cell's properties.
   * @param  {Boolean?} showFormulas  The user's setting
   * @param  {ColumnConstraint?} columnConstraint
   * @return {FormulaAlignment}       The cell's alignment: `left` | `center` | `right`.
   */
  alignCell(
    cell: Cell,
    evaluatedCell: EvaluatedCell,
    showFormulas?: boolean,
    columnConstraint?: ColumnConstraint | null
  ): FormulaAlignment {
    if (Boolean(showFormulas) && TableUtil.isCellFormula(cell)) {
      return 'left';
    }
    if (cell.align != null) {
      return cell.align;
    }
    if (!isTypedValueEmpty(evaluatedCell.value)) {
      if (
        isNumeric(evaluatedCell.value) ||
        columnConstraint?.columnDefinition?.type === TableColumnType.MEASUREMENT_AMOUNT
      ) {
        return 'right';
      } else if (isBaseBoolean(evaluatedCell.value)) {
        return 'center';
      }
    }
    return 'left';
  },

  /**
   * Return the dimension's corresponding selection constant.
   */
  dimensionToSelection(dimension: TableDimension): string {
    switch (dimension) {
      case TABLE_ROW:
        return TABLE_SELECTION_ROW;
      case TABLE_COLUMN:
        return TABLE_SELECTION_COLUMN;
      default:
        throw new Error(`invalid dimension: ${dimension}`);
    }
  },

  // Returns if a cell (i, j) is not rendered (i.e. exists within a merged region).
  // Returns false for all non-merged cells and merged cells at the top left of a
  // merged region.
  isNotRendered(i: number, j: number, evaluatedCell: EvaluatedCell): boolean {
    if (evaluatedCell.mergedBox != null) {
      return !(evaluatedCell.mergedBox.minI === i && evaluatedCell.mergedBox.minJ === j);
    }
    return false;
  },

  // Returns if a cell (i, j) is a rendered merged cell (i.e. the merged cell at the top
  // left corner of a merged region, or the top left rendered cell of the merged region).
  // Note that this is not the inverse of the isNotRendered method.
  isRenderedMergedCell(i: number, j: number, evaluatedCell?: EvaluatedCell): boolean {
    if (evaluatedCell?.mergedBox != null) {
      return evaluatedCell.mergedBox.minI === i && evaluatedCell.mergedBox.minJ === j;
    }
    return false;
  },

  // returns the rowSpan for a merged cell
  // If the cell is the top left cell in a merge, then it returns the cell's mergeRowSpan
  // if the cell is not the first row, then it will return the row span from this cell
  // to the end of the merged box
  getRowSpan(i: number, cell: Cell, evaluatedCell: EvaluatedCell): number | undefined {
    if (!evaluatedCell.mergedBox) {
      return undefined;
    }
    return cell.mergeRowSpan ?? (evaluatedCell.mergedBox ? evaluatedCell.mergedBox.maxI - i + 1 : undefined);
  },

  // returns the colSpan for a merged cell
  // If the cell is in the first column of the merge, then it returns the mergeColSpan
  // Otherwise, it will return undefined
  getColSpan(j: number, cell: Cell, evaluatedCell: EvaluatedCell): number | undefined {
    if (!evaluatedCell.mergedBox) {
      return undefined;
    }
    return (
      cell.mergeColSpan ??
      (evaluatedCell.mergedBox.minJ === j
        ? evaluatedCell.mergedBox.maxJ - evaluatedCell.mergedBox.minJ + 1
        : undefined)
    );
  },

  // Returns if a cell (i, j) is rendered in the region determined by `start` and `end`.
  // Returns false if the cell is out of bounds in the given region.
  isRenderedInRegion(
    i: number,
    j: number,
    evaluatedCell: EvaluatedCell,
    start: Array<number>,
    end: Array<number>
  ): boolean {
    const [i1, j1] = Array.from(start);
    const [i2, j2] = Array.from(end);
    if (i1 <= i && i <= i2 && j1 <= j && j <= j2) {
      if (evaluatedCell.mergedBox != null) {
        return evaluatedCell.mergedBox.minI === i && evaluatedCell.mergedBox.minJ === j;
      } else {
        return true;
      }
    }
    return false;
  },

  /**
   * Returns the position of a merged cell that crosses the given line in the given dimension.
   * If no partial merged cell crosses the line, returns null.
   *
   * @param table Computed table
   * @param dimension The dimension of the line, ROW if horizontal, COLUMN if vertical
   * @param index The index of the line. For example, index=1 means the line in between row index 0 and 1.
   * @param options Additional options to customize behavior
   * @returns [min row index, max row index] or [min column index, max column index] of one of the merged
   *          cells that crosses the line, or null if no partial merged cell.
   */
  hasMergedCellCrossingLine(
    table: EvaluatedTable,
    dimension: TableDimension,
    index: number,
    options: {
      /** Specify any columnIndices that should be ignored for checking for merges. Single Inputs notably
       * are often ignored.
       */
      ignoredColumnIndices?: Array<number>;
    } = {}
  ): [minIndex: number, maxIndex: number] | null {
    // The line at the beginning or end of the table will never have a partial merged cell.
    if (index <= 0 || index >= (dimension === TableDimension.ROW ? table.length : table[0].length)) {
      return null;
    }
    if (dimension === TableDimension.ROW) {
      for (const i of [index - 1, index]) {
        for (let j = 0; j < table[0].length; j++) {
          if (!options.ignoredColumnIndices?.includes(j) && table[i][j].mergedBox) {
            const {minI, maxI} = table[i][j].mergedBox!;
            if (i === index - 1 ? maxI >= index : minI < index) {
              return [minI, maxI];
            }
          }
        }
      }
    } else {
      for (const j of [index - 1, index]) {
        for (let i = 0; i < table.length; i++) {
          if (table[i][j].mergedBox) {
            const {minJ, maxJ} = table[i][j].mergedBox!;
            if (j === index - 1 ? maxJ >= index : minJ < index) {
              return [minJ, maxJ];
            }
          }
        }
      }
    }
    return null;
  },

  // Comparator for sorting merged boxes by row, first by minI and then by maxI.
  rowCmpMergedBoxes(a: MultiSelectionBox, b: MultiSelectionBox): number {
    if (a.minI !== b.minI) {
      return a.minI - b.minI;
    } else {
      return a.maxI - b.maxI;
    }
  },

  // Comparator for sorting merged boxes by column, first by minJ and then by maxJ.
  colCmpMergedBoxes(a: MultiSelectionBox, b: MultiSelectionBox): number {
    if (a.minJ !== b.minJ) {
      return a.minJ - b.minJ;
    } else {
      return a.maxJ - b.maxJ;
    }
  },

  withColumnNameSet(table: Table, i: number, label: string): Table {
    return reactUpdate(table, {columnLabels: {$splice: [[i, 1, label]]}});
  },

  allowColumnEdits: (table: Table): boolean =>
    !TableUtil.crossCompatibleLockedDimsCheck(table.lockedDims) || Boolean(table.allowColumnEdits),

  /**
   * Determine if the table has locked dimensions. Unlocked dimensions allow all editing operations.
   * To allow some but not all, lockedDims should be true and specific operations can be enabled with
   * other flags.
   *
   * Returns true if the table has ANY locked dimensions
   */
  hasLockedDims: (table: Table): boolean => TableUtil.crossCompatibleLockedDimsCheck(table.lockedDims),

  /**
   * If true, we disallow renaming columns and inserting columns not provided by
   * insertableColumnInfos.
   */
  disallowCustomColumns: (table: Table): boolean => Boolean(table.disallowCustomColumns),

  /** Keep in sync with benchling/notebook/plain_tables/validation.py:should_allow_add_rows */
  allowAddRows: (table: Table): boolean =>
    !TableUtil.crossCompatibleLockedDimsCheck(table.lockedDims) || Boolean(table.allowAddRows),

  allowNoRows: (table: Table): boolean => Boolean(table.allowNoRows),

  getDimensions: (table: Table): [number, number] => [table.table.length, table.widths.length],

  /** Get a boolean lockedDims value, even if the given value is an object of booleans.
   * This is a fix for the miniscule number of plain tables that got the wrong lockedDims shape
   * during INCDT-518. It should be removed after that refactor is fixed and re-deployed, and any
   * relevant data migrations have completed.
   *
   * Keep in sync with benchling/notebook/plain_tables/validation.py:cross_compatible_locked_dims_check
   *
   * @param lockedDims The lockedDims property on the table in question. Should be a boolean,
   * but may be an object if it's a plain table well plate that was created during a specific 8 hour window.
   */
  crossCompatibleLockedDimsCheck: (lockedDims: Optional<boolean | TableDimensionLocks>): boolean => {
    if (lockedDims == null) {
      return false;
    }
    if (typeof lockedDims === 'boolean') {
      return lockedDims;
    } else {
      return !(lockedDims.allowAddRows && lockedDims.allowRemoveRows && lockedDims.allowEditCols);
    }
  },

  crossCompatibleLockedColumnDimCheck: (lockedDims: Optional<boolean | TableDimensionLocks>): boolean => {
    if (lockedDims == null) {
      return false;
    }
    if (typeof lockedDims === 'boolean') {
      return lockedDims;
    } else {
      return !lockedDims.allowEditCols;
    }
  },

  crossCompatibleLockedRowDimCheck: (lockedDims: Optional<boolean | TableDimensionLocks>): boolean => {
    if (lockedDims == null) {
      return false;
    }
    if (typeof lockedDims === 'boolean') {
      return lockedDims;
    } else {
      return !(lockedDims.allowAddRows && lockedDims.allowRemoveRows);
    }
  },

  /**
   * Returns the table metadata of a table if it is a structured table.
   * Returns null if `table` is a plain table or other non-structured table.
   *
   * Note: "table metadata" is different from "structured table metadata",
   * which is {@link TableUtil.getStructuredTableMetadataFromTable}
   */
  getTableMetadataFromStructuredTable: (table: Table): StructuredTableToTableMetadata<unknown> | null =>
    table.metadata?.hasOwnProperty('kind')
      ? (table.metadata as StructuredTableToTableMetadata<unknown>)
      : null,

  /**
   * Returns the structured table metadata of a table if it is a structured table.
   * Returns null if `table` is a plain table or other non-structured table.
   *
   * Note: "structured table metadata" is different from "table metadata",
   * which is {@link TableUtil.getTableMetadataFromStructuredTable}
   */
  getStructuredTableMetadataFromTable(table: Table): StructuredTableMetadata | null {
    return this.getTableMetadataFromStructuredTable(table)?.metadata ?? null;
  },

  /**
   * Returns the structured table kind of a table if `table` is a structured table, otherwise returns null.
   */
  getTableKind(table: Table): StructuredTableKind | null {
    return this.getTableMetadataFromStructuredTable(table)?.kind ?? null;
  },

  /**
   * @deprecated This is a legacy field. Please see ColumnConstraint.isColumnUserConfigurable for current method of
   * defining this behavior.
   *
   * If specified, all columns before this index cannot be deleted, and new columns cannot be inserted
   * in that region. If null/undefined, there is no such restriction.
   */
  legacyFirstEditableColumnIndex: (table: Table): Optional<number> => table.legacyFirstEditableColumnIndex,

  lockedRowIndices(table: Table): Array<number> | null {
    const data = table.rowData;
    const tableKind = this.getTableKind(table);

    // Some table types allow deletion of finalized rows
    const allRowsUnlocked =
      (BenchlingConfig.ELN_ENABLE_LOOKUP_TABLE_MAPPING && tableKind === StructuredTableKind.LOOKUP) ||
      [
        StructuredTableKind.PROCEDURE_MATERIAL_INPUTS,
        StructuredTableKind.PROCEDURE_MATERIAL_OUTPUTS,
        StructuredTableKind.PROCEDURE_DATA_PARAMETERS_CONFIG,
        StructuredTableKind.PROCEDURE_DATA_PARAMETERS_CONFIG_WITH_PARAM_LIBRARY,
        StructuredTableKind.PROCEDURE_EQUIPMENT,
        StructuredTableKind.TEST_DEFINITIONS,
        StructuredTableKind.STOICHIOMETRY,
      ].includes(tableKind!);

    if (allRowsUnlocked) {
      return data ? [] : null;
    }

    return data
      ? range(0, data.length - 1).filter((i) => data[i].isFinalized && !data[i].uiData?.archivePurpose)
      : null;
  },

  /**
   * Return a boolean indicating if a `table` can be transformed into `newTable` given the constraints that
   * apply to the table. Also, warn if the transformation is invalid.
   *
   * Currently this only checks if the dimensions have changed on a locked-dimension table.
   *
   * @param {TableConstants.table} table - original table
   * @param {TableConstants.table} newTable - transformed table
   * @param {Object} - Allow specification of `forColumnEdit` to handle a `table` with `allowColumnEdits`
   * @return {boolean}
   */
  isInvalidOpWithWarn(table: Table, newTable: Table, param: TableOpOptions = {}): boolean {
    const {forRowRemoval, forColumnEdit, ignoreLockedDimensions} = param;
    if (!ignoreLockedDimensions && TableUtil.hasLockedDims(table)) {
      const dims1 = TableUtil.getDimensions(table);
      const dims2 = TableUtil.getDimensions(newTable);
      let invalidRowChange = false;
      if (dims1[0] > dims2[0]) {
        invalidRowChange = !(forRowRemoval && table.allowRowRemoval);
      } else if (dims1[0] < dims2[0]) {
        invalidRowChange = !table.allowAddRows;
      }
      const invalidColChange =
        dims1[1] !== dims2[1] && !(forColumnEdit && (table.allowColumnEdits || table.allowLookupColumnEdits));
      if (invalidRowChange || invalidColChange) {
        if (table.maxRows != null && dims2[0] > table.maxRows) {
          notifications.warn(
            <>
              Unable to complete operation: <strong>{table.name}</strong> cannot have more than
              {table.maxRows} rows
            </>,
            {
              id: `TableUtil.isInvalidOpWithWarn:${table.name}`,
            }
          );
        } else {
          notifications.warn(
            <>
              Unable to complete operation: <strong>{table.name}</strong> has locked dimensions.
            </>,
            {
              id: `TableUtil.isInvalidOpWithWarn:${table.name}`,
            }
          );
        }
        return true;
      }
    }
    return false;
  },

  mapCellText(table: Table, mapFn: (s: string, i: number, j: number) => string): Table {
    return {
      ...table,
      table: table.table.map((row, i) =>
        row.map((cell, j) => ({
          ...cell,
          text: mapFn(cell.text, i, j),
        }))
      ),
    };
  },

  makeEmptyCell(properties?: {precision?: number}): Cell {
    if (properties != null) {
      const propertyKeys = Object.keys(properties);
      assertAndContinue(
        propertyKeys.length === 0 || (propertyKeys.length === 1 && propertyKeys[0] === 'precision'),
        `Unexpected properties on empty cell: ${JSON.stringify(propertyKeys)}`
      );
    }
    return {text: '', ...properties};
  },

  makeCellWithText(text: string, properties?: {precision?: number}): Cell {
    return {...this.makeEmptyCell(properties), text};
  },

  /**
   * Return the rendered column name (this is the JSX is rendered in the axis cell)
   */
  getRenderedColumnLabel(table: Table, j: number): React.ReactNode {
    const {columnConstraints} = table;
    return columnConstraints?.[j]?.renderedLabel || TableUtil.getColumnLabel(table, j);
  },

  /**
   * Return the set column name (which will be the basis for renaming the column)
   */
  getColumnLabel(table: Table, j: number): string | null {
    if (!table.columnLabels || table.columnLabels[j] == null) {
      return null;
    }
    return table.columnLabels[j];
  },

  getColumnDefinition(table: Table, j: number): ColumnDefinition | null {
    const {columnConstraints} = table;
    return columnConstraints?.[j]?.columnDefinition ?? null;
  },

  getColumnDefinitionType(table: Table, j: number): TableColumnType | null {
    const columnDefinition = TableUtil.getColumnDefinition(table, j);
    return columnDefinition?.type ?? null;
  },

  /**
   * Lines up with makeEmptyCell and notebook/structured-tables/util/getBlankRow.ts
   *
   * Keep in sync with is_cell_empty in read_cell.py on the backend.
   *
   * This function checks for empty text, null typed value, and no extraneous properties,
   * but it could use improvement as a true "empty" check, so we may want to implement an
   * improved version and migrate usages over. In particular:
   * - It should probably use isTypedValueEmpty if there is a typed value.
   * - Consistent with isTypedValueEmpty, it may want to treat whitespace-only strings as empty.
   * - The extraneous property check was originally meant to exactly match makeEmptyCell, but
   *   has evolved since then and isn't actually necessary to see if the cell is conceptually
   *   empty. It can probably be removed.
   */
  isCellEmpty(cell: Cell): boolean {
    return (
      cell.text === '' &&
      Object.keys(cell).every((key) =>
        [
          'customEval',
          'customEvalType',
          'customEvalArgs',
          'customEvalOptions',
          'text',
          'typedValue',
          'bold',
          'underline',
          'italic',
          'strikethrough',
          'precision',
          'align',
          'foregroundColor',
          'backgroundColor',
          'grayOutContents',
          'important',
          'scientificNotation',
          'locked',
        ].includes(key)
      ) &&
      cell.typedValue == null
    );
  },

  /** Create a given number of blank rows for the given table.
   * Uses the given table to create the new rows by copying the last row of the table and replacing
   * the cells with empty cells, but with the precision of the cell maintained.
   * NOTE: Does NOT check for locked or max capacity tables.
   *
   * @param numRows The number of blank rows to create.
   * @returns An array of blank rows that can be appended to the given table.
   */
  makeBlankRows(table: Table, numRows: number): Cells {
    // Copy last row's precision
    const lastRow: Row = table.table[table.table.length - 1];
    const newRow = lastRow.map((cell: Cell) =>
      TableUtil.makeEmptyCell(cell.precision != null ? {precision: cell.precision} : {})
    );
    return Array<Row>(numRows).fill(newRow);
  },

  /** Merges a given selection of a table, preserving the top-left cell and clearing all other cells out.
   *
   * @returns a new table with the specified region merged.
   */
  mergeTableRegion(table: Table, {minI, maxI, minJ, maxJ}: MultiSelectionBox): Table {
    return produce(table, (draft) => {
      for (let rowIndex = minI; rowIndex <= maxI; rowIndex++) {
        for (let colIndex = minJ; colIndex <= maxJ; colIndex++) {
          const cell = draft.table?.[rowIndex]?.[colIndex];
          // if the specified cell doesn't actually exist in the table, skip it
          if (!cell) {
            continue;
          }
          // if it's the first cell in the merge range (most top-left cell), set the mergeRowSpan and
          // mergeColSpan to span the full range
          if (rowIndex === minI && colIndex === minJ) {
            cell.mergeRowSpan = maxI - minI + 1;
            cell.mergeColSpan = maxJ - minJ + 1;
          } else {
            // otherwise, clear out the cell
            draft.table[rowIndex][colIndex] = TableUtil.makeEmptyCell();
          }
        }
      }
    });
  },

  /** Merges each single input column of a table.
   *
   * @param table The table to be force-merged for its single input columns.
   * @returns a new table with the Single Input columns fully remerged.
   */
  remergeSingleInputColumns(table: Table): Table {
    const singleInputBoundaryIndex = TableUtil.findSingleInputBoundaryIndex(table);
    let tableWithMerges = table;
    for (let columnIndex = 0; columnIndex < singleInputBoundaryIndex; columnIndex++) {
      const lastRowIndex = tableWithMerges.table.length - 1;
      tableWithMerges = TableUtil.mergeTableRegion(tableWithMerges, {
        minI: 0,
        maxI: lastRowIndex,
        minJ: columnIndex,
        maxJ: columnIndex,
      });
    }
    return tableWithMerges;
  },

  isValidFormula(text: string): boolean {
    return text.startsWith('=');
  },

  isCellFormula(cell: Cell): boolean {
    return cell && cell.text != null && TableUtil.isValidFormula(cell.text);
  },

  augmentTableWithColumnLabels(
    table: Table,
    computedTable: Array<Array<EvaluatedCell>>
  ): {table: Table; computedTable: Array<Array<EvaluatedCell>>} {
    if (table.columnLabels.every((label) => label == null)) {
      return {table, computedTable};
    }

    const headerRow = table.columnLabels.map((label) => ({text: label || ''}));
    const computedHeaderRow = table.columnLabels.map(
      (label, colIdx: number): EvaluatedCell => ({
        value: {type: BaseTypedValueType.STRING, rawValue: label || ''},
        locked: table.columnConstraints[colIdx]?.columnDefinition?.locked ?? false,
      })
    );
    return {
      table: {...table, table: [headerRow, ...table.table]},
      computedTable: [computedHeaderRow, ...computedTable],
    };
  },

  tableEditableToHTML(fullTable: Table, computedTable: Array<Array<EvaluatedCell>>): JQuery {
    ({table: fullTable, computedTable} = TableUtil.augmentTableWithColumnLabels(fullTable, computedTable));
    return makeTableHTML(fullTable, computedTable, {
      minI: 0,
      minJ: 0,
      maxI: fullTable.table.length - 1,
      maxJ: fullTable.table[0].length - 1,
    });
  },

  columnLabelToText(label: string | null, index: number): string {
    return label == null ? excess.util.colNumberToLetters(index) : label;
  },

  columnLabelsToText(columnLabels: Array<string | null>): Array<string> | null {
    return columnLabels.some((label) => label != null)
      ? columnLabels.map((label, index) => TableUtil.columnLabelToText(label, index))
      : null;
  },

  tableEditableToText(fullTable: Table, computedTable: Array<Array<EvaluatedCell>>): string {
    const headerRow = TableUtil.columnLabelsToText(fullTable.columnLabels);
    const rows =
      headerRow != null
        ? getPlainTextTable({
            table: fullTable.table,
            computedTable,
            includeCellValueModifiers: false,
            headerRow,
          })
        : getPlainTextTable({
            table: fullTable.table,
            computedTable,
            includeCellValueModifiers: false,
          });
    return ExportUtil.formatRows(rows, '\t');
  },

  __range__(left: number, right: number, inclusive: boolean): Array<number> {
    const range = [];
    const ascending = left < right;
    const end = !inclusive ? right : ascending ? right + 1 : right - 1;
    for (let i = left; ascending ? i < end : i > end; ascending ? i++ : i--) {
      range.push(i);
    }
    return range;
  },

  isMonospaced(columnConstraint: ColumnConstraint | null): boolean {
    if (columnConstraint?.columnDefinition == null) {
      return false;
    }
    return [
      TableColumnType.DNA_BASES,
      TableColumnType.RNA_BASES,
      TableColumnType.DNA_HELM,
      TableColumnType.RNA_HELM,
      TableColumnType.DNA_IDT,
      TableColumnType.RNA_IDT,
    ].includes(columnConstraint.columnDefinition.type);
  },

  isColumnLocked(columnConstraint: ColumnConstraint | null): boolean {
    return (
      columnConstraint?.isProtected ||
      columnConstraint?.columnDefinition?.locked ||
      columnConstraint?.columnDefinition?.type === TableColumnType.DEDICATED_LOOKUP
    );
  },

  getColumnDefinitionsFromTable(table: TableData): Array<ColumnDefinition> {
    return table.columnConstraints.map(
      (columnConstraint) => columnConstraint?.columnDefinition as ColumnDefinition
    );
  },

  getStructuredTableColumnsFromTable(table: TableData): Array<StructuredTableColumn> {
    return table.columnConstraints.map(
      (columnConstraint) => columnConstraint?.structuredTableColumn as StructuredTableColumn
    );
  },

  /**
   * Returns the number of columns in the selection that support setting precision.
   * For example, a float column supports precision, but not a string column.
   * Note: All plain table columns support setting precision.
   *
   * @param table Plain or structured table
   * @param minJ Index of first column in selection, inclusive
   * @param maxJ Index of last column in selection, inclusive
   * @returns Number of columns in selection that support setting precision
   */
  getNumberOfColumnsSupportingPrecision(table: Table, minJ: number, maxJ: number): number {
    const tableMetadata = TableUtil.getTableMetadataFromStructuredTable(table) ?? undefined;
    return TableUtil.getColumnDefinitionsFromTable(table)
      .slice(minJ, maxJ + 1)
      .filter((columnDefinition: ColumnDefinition | null) => {
        if (!columnDefinition) {
          // Plain table column can have numbers
          return true;
        }

        // This column has a numeric data type, but does not support precision. See CUST-8783.
        if (columnDefinition.type === TableColumnType.MEASUREMENT_AMOUNT) {
          return false;
        }

        const dataType = guessColumnDataType(columnDefinition, tableMetadata)?.type;
        return (
          dataType === ColumnDataType.DECIMAL ||
          dataType === ColumnDataType.INTEGER ||
          dataType === ColumnDataType.ROW_DELEGATE // Can potentially contain numbers
        );
      }).length;
  },

  /**
   * Scans through the StructuredTableColumns to find contiguous columns that are single inputs.
   * Returns the indices of the start and end for such columns.
   */
  getSingleInputIndexRange(
    columns: Array<StructuredTableColumn> | Array<GenericStructuredTableColumn<ClientId>>
  ): [startIndex: number, endIndex: number] | null {
    let startIndex: number | null = null;
    let endIndex: number | null = null;

    for (let i = 0; i < columns.length; i++) {
      if (columns[i]?.isSingleInput) {
        if (startIndex == null) {
          startIndex = i;
        }
        endIndex = i;
      } else if (startIndex != null) {
        break;
      }
    }
    return startIndex != null && endIndex != null ? [startIndex, endIndex] : null;
  },

  /**
   * Returns the boundary index (the first index that is not a single input) between single inputs and regular columns
   * Since single input columns must be at the beginning of the table, this is equivalent to the number of single inputs.
   */
  findSingleInputBoundaryIndex(table: Table): number {
    const index = TableUtil.getStructuredTableColumnsFromTable(table).findIndex(
      (column) => !column?.isSingleInput
    );
    return index === -1 ? table.columnConstraints.length : index;
  },

  isStructuredTable(tableType: Optional<string>): boolean {
    return tableType != null && tableType.startsWith('structured_table');
  },

  isLegacyTable(tableType: Optional<string>): boolean {
    const types: Array<string> = [
      // Legacy registration table
      ModelTypeConstants.REGISTRATION_TABLE,
      ModelTypeConstants.REGISTRATION_TABLE_CONFIG,
      // Legacy result table
      ModelTypeConstants.DATA_TABLE,
      ModelTypeConstants.DATA_TABLE_CONFIG,
      // Legacy transfer table
      ModelTypeConstants.TRANSFER_TABLE,
      ModelTypeConstants.TRANSFER_TABLE_CONFIG,
      ModelTypeConstants.TRANSFER_TABLE_PLACEHOLDER,
    ];
    return tableType != null && types.includes(tableType);
  },

  getTableMultiSelectKey(tableCid: string): string {
    return `multiSelect-${tableCid}`;
  },

  /**
   * The editor should be rendered below the header row, and completely within the bounds
   * of the table.
   */
  getTableCellEditorPosition(
    scrollContainerRect: DOMRect,
    activeCellRect: DOMRect,
    headerRowHeight: number,
    isSingleInput: boolean,
    isStickyHeaderVisible: boolean,
    stickyHeaderRect?: DOMRect | null
  ): TableCellEditorPosition {
    // This is the bottom of the visible header, including the column header row.
    // If the sticky header is visible, then this should be the bottom of that, which already includes the
    // header row height.
    const yBottomOfHeader =
      isStickyHeaderVisible && stickyHeaderRect
        ? stickyHeaderRect.y + stickyHeaderRect.height
        : scrollContainerRect.y + headerRowHeight;
    // The table cell editor should always be below the bottom of the header (except for Single Inputs)
    const topOffsetDueToHeader =
      !isSingleInput && yBottomOfHeader > activeCellRect.y ? yBottomOfHeader - activeCellRect.y : 0;
    const top = activeCellRect.top + topOffsetDueToHeader;
    // The table cell editor should always be within the visible horizontal scroll
    const left = Math.max(activeCellRect.left, scrollContainerRect.left);
    const right = Math.min(activeCellRect.right, scrollContainerRect.right);

    // We need to account for the width that the border occupies in the cell editor, since we cannot
    // use a box shadow to show the selection and have a border outside the scrollbar. This also includes the
    // divider border that is between the cells.
    const width = right - left - 2 * TABLE_DOUBLE_SELECTED_BORDER_WIDTH - TABLE_CELL_BORDER_WIDTH;

    // The table cell editor should not extend beyond the visible bottom of the vertical scroll container
    const maxHeight = scrollContainerRect.bottom - top;

    // The table cell editor should be at least as tall as the cell it is editing (merged cell height).
    const minHeight = Math.min(activeCellRect.height, maxHeight);
    return {
      top,
      left,
      width,
      minHeight,
      maxHeight,
    };
  },

  /**
   * Returns all plain, structured, mixture prep ingredients, and lab auto output tables from a note.
   * @param addRunSchemaName If true, adds the run schema name to the lab auto output table names to differentiate when using the same instrument
   * @param removeMixtureIngredientSuffix If true, removes the auto-appended "ingredients" suffix from the mixture prep ingredients table name
   */
  getTablesFromNote(
    note: Note,
    {
      addRunSchemaName,
      removeMixtureIngredientSuffix,
    }: {addRunSchemaName?: boolean; removeMixtureIngredientSuffix?: boolean} = {}
  ): Array<Table | AnyStructuredTable> {
    const tables = [];
    for (const notePart of note?.noteParts ?? []) {
      const item = notePart.item as NoteLinkedObject;
      if (isPlainTable(notePart)) {
        // Plain table
        tables.push(notePart.table as Table);
      } else if (hasNLO(notePart) && isStructuredTable(item)) {
        // Structured table
        tables.push(item as AnyStructuredTable);
      } else if (isMixtureGroup(item)) {
        for (const notePart of item.containedNoteParts) {
          if (isNotePartWithItem(notePart) && isMixtureIngredientsStructuredTable(notePart.item)) {
            // Mixture prep ingredients table
            tables.push({
              ...notePart.item,
              name: removeMixtureIngredientSuffix
                ? notePart.item.name.replace(/ ingredients$/, '')
                : notePart.item.name,
            });
          }
        }
      } else if (hasNLO(notePart) && isNoteLinkedAssayData(item)) {
        // Lab auto output tables
        tables.push(
          ...AutomationFileUtil.getAutomationOutputStructuredTables(item as NoteLinkedAssayData, {
            addRunSchemaName,
          })
        );
      }
    }

    return tables.filter((table) => table != null);
  },

  /**
   * Returns true if the note is missing an evaluation snapshot and needs full evaluation.
   */
  isMissingEvaluationSnapshot(note: Note): boolean {
    return note.noteRuntimeSnapshot?.evaluationSnapshot == null;
  },

  getStructuredTableColumnsForTemplates(
    table: Cells,
    columns: Array<StructuredTableColumn>
  ): Array<StructuredTableColumn> {
    if (table.length === 1) {
      // For tables with 1 row, set existing values as the default values for the columns
      const firstRow = table[0];
      assert(firstRow && firstRow.length === columns.length, 'Table must have one complete row');
      return columns.map((column, colIndex) => ({
        ...column,
        defaultValue: omit(firstRow[colIndex], ...CELL_UI_PROPERTIES),
      }));
    } else {
      return columns;
    }
  },

  getTableDimensionsFromTable(table: Table): {rowCount: number; columnCount: number; cellCount: number} {
    const rowCount = table.table.length;
    const columnCount = TableUtil.getColumnCount(table, false);
    const cellCount = rowCount * columnCount;

    return {rowCount, columnCount, cellCount};
  },

  getTableDimensionsFromStructuredTable(table: AnyStructuredTable): {
    rowCount: number;
    columnCount: number;
    cellCount: number;
  } {
    const rowCount = table.rows.length;
    const columnCount = table.columns.length;
    const cellCount = rowCount * columnCount;
    return {rowCount, columnCount, cellCount};
  },

  makeCell: (text: string, properties: Partial<Cell> = {}): Cell => ({text, ...properties}),

  isIndexBetween: (firstIndex: TableIndex, secondIndex: TableIndex, indexToCheck: TableIndex): boolean => {
    const [firstRow, firstColumn] = firstIndex;
    const [secondRow, secondColumn] = secondIndex;
    const [row, column] = indexToCheck;
    return (
      ((firstRow <= row && row <= secondRow) || (secondRow <= row && row <= firstRow)) &&
      ((firstColumn <= column && column <= secondColumn) || (secondColumn <= column && column <= firstColumn))
    );
  },
};

export default TableUtil;
